import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, ChefHat, Save, Plus, Trash2, GripVertical, Eye,
  Flame, Snowflake, Waves, UtensilsCrossed, Sparkles, CookingPot, CheckCircle2,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_OPTIONS = [
  { value: 'Flame', label: 'Flamme' },
  { value: 'CookingPot', label: 'Marmite' },
  { value: 'Snowflake', label: 'Flocon (froid)' },
  { value: 'Waves', label: 'Eau / Lave-vaisselle' },
  { value: 'UtensilsCrossed', label: 'Ustensiles' },
  { value: 'Sparkles', label: 'Nettoyage' },
  { value: 'ChefHat', label: 'Chef' },
  { value: 'CheckCircle2', label: 'Check' },
];

const ICON_MAP = { Flame, CookingPot, Snowflake, Waves, UtensilsCrossed, Sparkles, ChefHat, CheckCircle2 };

function IconPreview({ name, className }) {
  const Icon = ICON_MAP[name] || ChefHat;
  return <Icon className={className} strokeWidth={1.5} />;
}

export default function AdminKitchen() {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const api = useCallback((method, url, data) =>
    axios({ method, url: `${BACKEND_URL}${url}`, data, headers: { Authorization: `Bearer ${token}` } }),
  [token]);

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!token) navigate('/admin');
  }, [token, navigate]);

  useEffect(() => {
    api('get', '/api/admin/kitchen-page')
      .then(r => setForm(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const { key, _id, updated_at, cuisine_per_day, ...payload } = form;
      await api('put', '/api/admin/kitchen-page', payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
    setSaving(false);
  };

  const updateField = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const updateEquipment = (index, field, value) => {
    setForm(f => {
      const list = [...(f.equipment_list || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...f, equipment_list: list };
    });
  };

  const addEquipment = () => {
    setForm(f => ({
      ...f,
      equipment_list: [...(f.equipment_list || []), { icon: 'ChefHat', label: '', description: '' }],
    }));
  };

  const removeEquipment = (index) => {
    setForm(f => ({
      ...f,
      equipment_list: (f.equipment_list || []).filter((_, i) => i !== index),
    }));
  };

  const updateGalleryImage = (index, field, value) => {
    setForm(f => {
      const list = [...(f.gallery_images || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...f, gallery_images: list };
    });
  };

  const addGalleryImage = () => {
    setForm(f => ({
      ...f,
      gallery_images: [...(f.gallery_images || []), { url: '', caption: '' }],
    }));
  };

  const removeGalleryImage = (index) => {
    setForm(f => ({
      ...f,
      gallery_images: (f.gallery_images || []).filter((_, i) => i !== index),
    }));
  };

  const updateTestimonial = (index, field, value) => {
    setForm(f => {
      const list = [...(f.testimonials || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...f, testimonials: list };
    });
  };

  const addTestimonial = () => {
    setForm(f => ({
      ...f,
      testimonials: [...(f.testimonials || []), { text: '', author: '', role: '' }],
    }));
  };

  const removeTestimonial = (index) => {
    setForm(f => ({
      ...f,
      testimonials: (f.testimonials || []).filter((_, i) => i !== index),
    }));
  };

  if (loading || !form) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#8A9A5B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="bg-[#2C3E30] text-[#F2F0E9] px-6 md:px-12 py-4 sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} data-testid="admin-kitchen-back" className="text-[#F2F0E9]/70 hover:text-[#F2F0E9]">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <h1 className="font-['Playfair_Display'] text-lg md:text-xl flex items-center gap-2">
              <ChefHat className="w-5 h-5 text-[#C4A962]" strokeWidth={1.5} />
              Page Cuisine
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" data-testid="kitchen-preview-btn" onClick={() => window.open('/cuisine-professionnelle', '_blank')} className="text-[#F2F0E9]/70 hover:text-[#F2F0E9]">
              <Eye className="w-4 h-4 mr-1" /> Voir
            </Button>
            <Button data-testid="kitchen-save-btn" size="sm" onClick={handleSave} disabled={saving} className="bg-[#C4A962] hover:bg-[#b5945a] text-white rounded-full">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Sauvegarde...' : saved ? 'Sauvegarde !' : 'Sauvegarder'}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[900px] mx-auto px-6 md:px-12 py-8 space-y-10">
        {/* Hero Section */}
        <section className="bg-white rounded-2xl border border-[#E5E5E0] p-6 md:p-8">
          <h2 className="font-semibold text-[#2C3E30] text-lg mb-4">Section Hero</h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#1C1C1E] mb-1 block">Titre principal</label>
              <Input data-testid="kitchen-hero-title-input" value={form.hero_title || ''} onChange={e => updateField('hero_title', e.target.value)} className="rounded-lg" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1C1C1E] mb-1 block">Sous-titre</label>
              <Input data-testid="kitchen-hero-subtitle-input" value={form.hero_subtitle || ''} onChange={e => updateField('hero_subtitle', e.target.value)} className="rounded-lg" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1C1C1E] mb-1 block">URL de l'image hero</label>
              <Input data-testid="kitchen-hero-image-input" value={form.hero_image || ''} onChange={e => updateField('hero_image', e.target.value)} placeholder="https://..." className="rounded-lg" />
              {form.hero_image && (
                <img src={form.hero_image} alt="Hero preview" className="mt-2 rounded-lg h-32 object-cover w-full" />
              )}
            </div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="bg-white rounded-2xl border border-[#E5E5E0] p-6 md:p-8">
          <h2 className="font-semibold text-[#2C3E30] text-lg mb-4">Texte d'introduction</h2>
          <Textarea
            data-testid="kitchen-intro-input"
            value={form.intro_text || ''}
            onChange={e => updateField('intro_text', e.target.value)}
            className="rounded-lg min-h-[120px]"
            placeholder="Texte de presentation de la cuisine..."
          />
        </section>

        {/* Equipment Section */}
        <section className="bg-white rounded-2xl border border-[#E5E5E0] p-6 md:p-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-[#2C3E30] text-lg">Equipements</h2>
            <Button variant="outline" size="sm" data-testid="add-equipment-btn" onClick={addEquipment} className="rounded-full">
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          </div>
          <div className="space-y-4">
            {(form.equipment_list || []).map((eq, i) => (
              <div key={i} data-testid={`equipment-editor-${i}`} className="flex items-start gap-3 p-4 rounded-xl bg-[#FAFAF9] border border-[#E5E5E0]">
                <GripVertical className="w-4 h-4 text-[#C4A962]/40 mt-3 flex-shrink-0" />
                <div className="flex-shrink-0 mt-2">
                  <IconPreview name={eq.icon} className="w-5 h-5 text-[#C4A962]" />
                </div>
                <div className="flex-1 space-y-2">
                  <div className="grid grid-cols-3 gap-2">
                    <Select value={eq.icon || 'ChefHat'} onValueChange={v => updateEquipment(i, 'icon', v)}>
                      <SelectTrigger className="rounded-lg h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ICON_OPTIONS.map(o => (
                          <SelectItem key={o.value} value={o.value}>
                            <span className="flex items-center gap-2"><IconPreview name={o.value} className="w-3.5 h-3.5" /> {o.label}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input value={eq.label || ''} onChange={e => updateEquipment(i, 'label', e.target.value)} placeholder="Nom" className="rounded-lg h-9 text-sm col-span-2" />
                  </div>
                  <Input value={eq.description || ''} onChange={e => updateEquipment(i, 'description', e.target.value)} placeholder="Description courte" className="rounded-lg h-9 text-sm" />
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeEquipment(i)} className="text-red-400 hover:text-red-600 mt-2 flex-shrink-0">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </section>

        {/* Gallery Section */}
        <section className="bg-white rounded-2xl border border-[#E5E5E0] p-6 md:p-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-[#2C3E30] text-lg">Galerie photos</h2>
            <Button variant="outline" size="sm" data-testid="add-gallery-btn" onClick={addGalleryImage} className="rounded-full">
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(form.gallery_images || []).map((img, i) => (
              <div key={i} data-testid={`gallery-editor-${i}`} className="p-3 rounded-xl bg-[#FAFAF9] border border-[#E5E5E0]">
                {img.url && <img src={img.url} alt={img.caption} className="w-full h-28 object-cover rounded-lg mb-2" />}
                <Input value={img.url || ''} onChange={e => updateGalleryImage(i, 'url', e.target.value)} placeholder="URL de l'image" className="rounded-lg h-8 text-xs mb-1" />
                <div className="flex items-center gap-1">
                  <Input value={img.caption || ''} onChange={e => updateGalleryImage(i, 'caption', e.target.value)} placeholder="Legende" className="rounded-lg h-8 text-xs flex-1" />
                  <Button variant="ghost" size="sm" onClick={() => removeGalleryImage(i)} className="text-red-400 hover:text-red-600 h-8 px-2">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="bg-white rounded-2xl border border-[#E5E5E0] p-6 md:p-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-[#2C3E30] text-lg">Temoignages</h2>
            <Button variant="outline" size="sm" data-testid="add-testimonial-btn" onClick={addTestimonial} className="rounded-full">
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          </div>
          <div className="space-y-4">
            {(form.testimonials || []).map((t, i) => (
              <div key={i} data-testid={`testimonial-editor-${i}`} className="p-4 rounded-xl bg-[#FAFAF9] border border-[#E5E5E0]">
                <Textarea value={t.text || ''} onChange={e => updateTestimonial(i, 'text', e.target.value)} placeholder="Temoignage..." className="rounded-lg min-h-[60px] text-sm mb-2" />
                <div className="flex items-center gap-2">
                  <Input value={t.author || ''} onChange={e => updateTestimonial(i, 'author', e.target.value)} placeholder="Auteur" className="rounded-lg h-8 text-xs flex-1" />
                  <Input value={t.role || ''} onChange={e => updateTestimonial(i, 'role', e.target.value)} placeholder="Role (optionnel)" className="rounded-lg h-8 text-xs flex-1" />
                  <Button variant="ghost" size="sm" onClick={() => removeTestimonial(i)} className="text-red-400 hover:text-red-600 h-8 px-2">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Conditions Section */}
        <section className="bg-white rounded-2xl border border-[#E5E5E0] p-6 md:p-8">
          <h2 className="font-semibold text-[#2C3E30] text-lg mb-4">Conditions et tarifs</h2>
          <Textarea
            data-testid="kitchen-conditions-input"
            value={form.conditions_text || ''}
            onChange={e => updateField('conditions_text', e.target.value)}
            className="rounded-lg min-h-[100px]"
            placeholder="Conditions d'utilisation et tarifs..."
          />
          <p className="text-xs text-[#595959] mt-2">Le tarif par jour est gere depuis la page Tarifs (section Evenements).</p>
        </section>

        {/* Bottom save */}
        <div className="flex justify-end pb-8">
          <Button data-testid="kitchen-save-bottom-btn" onClick={handleSave} disabled={saving} className="bg-[#C4A962] hover:bg-[#b5945a] text-white rounded-full px-8">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Sauvegarde...' : 'Sauvegarder les modifications'}
          </Button>
        </div>
      </div>
    </div>
  );
}
